import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UniversalSearchBar } from './UniversalSearchBar';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon, TruckIcon, FileIcon, AlertTriangleIcon, RefreshCwIcon, ShieldIcon, BarChart3Icon, HelpCircleIcon, BellIcon, PlusIcon, LinkIcon, RepeatIcon, MoreHorizontalIcon, ArrowUpDownIcon } from 'lucide-react';
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const [reportingExpanded, setReportingExpanded] = useState(false);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationTab, setNotificationTab] = useState<'unread' | 'all'>('unread');
  const notificationsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const placeholderTexts = ['How do I activate new payment methods?', 'How long does it take to receive a verdict on a dispute?', 'Can I refund a successful transaction from the past week?', "How long can I place a temporary hold on a customer's card for?"];
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  const isBalanceActive = () => {
    return ['/balance', '/balance-activity', '/payouts'].some(path => location.pathname.startsWith(path));
  };
  const isTransactionsActive = () => {
    return ['/sales', '/disputes', '/refunds', '/payment-analytics'].some(path => location.pathname.startsWith(path));
  };
  const isReportingActive = () => {
    return ['/reports', '/custom-metrics', '/nadi'].some(path => location.pathname.startsWith(path));
  };
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/invoices') return 'Invoices';
    if (location.pathname.startsWith('/invoice-detail/')) return 'Invoice Details';
    if (location.pathname === '/invoice-form') return location.search.includes('id=') ? 'Edit Invoice' : 'Create Invoice';
    if (location.pathname === '/customers') return 'Customers';
    if (location.pathname.startsWith('/customer-detail/')) return 'Customer Details';
    if (location.pathname === '/customer-form') return location.search.includes('id=') ? 'Edit Customer' : 'Create Customer';
    if (location.pathname === '/sales') return 'Sales';
    if (location.pathname.startsWith('/sale-detail/')) return 'Sale Details';
    if (location.pathname === '/disputes') return 'Disputes';
    if (location.pathname.startsWith('/dispute-detail/')) return 'Dispute Details';
    if (location.pathname === '/refunds') return 'Refunds';
    if (location.pathname.startsWith('/refund-detail/')) return 'Refund Details';
    if (location.pathname === '/suppliers') return 'Suppliers';
    if (location.pathname.startsWith('/supplier-detail/')) return 'Supplier Details';
    if (location.pathname === '/supplier-form') return location.search.includes('id=') ? 'Edit Supplier' : 'Create Supplier';
    if (location.pathname === '/supplier-bulk-upload') return 'Bulk Upload Suppliers';
    if (location.pathname === '/bills') return 'Bills';
    if (location.pathname.startsWith('/bill-detail/')) return 'Bill Details';
    if (location.pathname === '/bill-form') return location.search.includes('id=') ? 'Edit Bill' : 'Create Bill';
    if (location.pathname.startsWith('/bill-approval/')) return 'Bill Approval';
    if (location.pathname.startsWith('/bill-financing/')) return 'Request Bill Financing';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    if (location.pathname === '/reports') return 'Reports';
    if (location.pathname === '/custom-metrics') return 'Custom Metrics';
    if (location.pathname === '/nadi') return 'Nadi';
    return 'Dashboard';
  };
  // Nav item: no background box, just text with icon. Active = blue text.
  const navItemClass = (active: boolean) => `w-full flex items-center px-3 py-1.5 text-[13px] rounded-md transition-colors ${active ? 'text-[#F7941D] font-medium' : 'text-gray-300 hover:text-white hover:bg-white/10'}`;
  const navParentClass = (active: boolean) => `w-full flex items-center justify-between px-3 py-1.5 text-[13px] rounded-md transition-colors ${active ? 'text-[#F7941D] font-medium' : 'text-gray-300 hover:text-white hover:bg-white/10'}`;
  const subNavItemClass = (active: boolean) => `w-full flex items-center px-3 py-1.5 text-[13px] transition-colors ${active ? 'text-[#F7941D] font-medium' : 'text-gray-400 hover:text-white'}`;
  // Close notifications on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const notifications = [{
    id: 1,
    message: 'Dispute #DP-2024-0001 requires your response. THB 100.01 fraudulent charge — deadline approaching.',
    date: 'Mar 15, 2026',
    unread: true,
    type: 'urgent'
  }, {
    id: 2,
    message: 'Dispute #DP-2024-0091 requires response. THB 1,538.94 duplicate charge — due in 5 hours.',
    date: 'Mar 15, 2026',
    unread: true,
    type: 'urgent'
  }, {
    id: 3,
    message: 'Bill from Acme Corp (INV-2026-003) is pending your approval. Due Mar 20, 2026.',
    date: 'Mar 14, 2026',
    unread: true,
    type: 'bill'
  }, {
    id: 4,
    message: 'Payment of THB 45,000 received from John Doe. Transaction completed successfully.',
    date: 'Mar 14, 2026',
    unread: true,
    type: 'payment'
  }, {
    id: 5,
    message: 'New refund processed for THB 2,500. Reason: Customer request. Refund ID #RF-0042.',
    date: 'Mar 13, 2026',
    unread: true,
    type: 'refund'
  }, {
    id: 6,
    message: 'Settlement completed successfully at 01:30 AM. All funds have been transferred.',
    date: 'Mar 13, 2026',
    unread: false,
    type: 'system'
  }, {
    id: 7,
    message: 'API uptime report: All systems operational. 99.99% uptime this month.',
    date: 'Mar 12, 2026',
    unread: false,
    type: 'system'
  }, {
    id: 8,
    message: 'New payment method integration available: Cryptocurrency payments now supported.',
    date: 'Mar 10, 2026',
    unread: false,
    type: 'system'
  }];
  const unreadNotifications = notifications.filter(n => n.unread);
  const displayNotifications = notificationTab === 'unread' ? unreadNotifications : notifications;
  return <div className="min-h-screen bg-[#F7F8FA]" data-id="element-1350">
      {/* Top Header Bar - spans FULL width */}
      <div className="fixed top-0 left-0 right-0 bg-white z-30 h-14" data-id="element-1351">
        <div className="flex items-center h-full" data-id="element-1352">
          {/* Logo section - exactly matches sidebar width */}
          <div className="hidden lg:flex items-center justify-center w-60 flex-shrink-0 h-full bg-[#0F1B2D] border-b border-[#0F1B2D]" data-id="element-1353">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-7 w-auto object-contain" data-id="element-1354" />
          </div>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center px-5 flex-shrink-0 h-full bg-[#0F1B2D] border-b border-[#0F1B2D]" data-id="element-1355">
            <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-7 w-auto object-contain" data-id="element-1356" />
          </div>

          {/* Separator + hamburger + search + right icons */}
          <div className="flex items-center flex-1 h-full border-l border-gray-200 border-b border-b-gray-200 px-5 gap-5" data-id="element-1357">
            {/* Hamburger menu */}
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0" data-id="element-1358">
              <MenuIcon size={20} data-id="element-1359" />
            </button>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl" data-id="element-1360">
              <UniversalSearchBar data-id="element-1361" />
            </div>

            {/* Right side */}
            <div className="flex items-center gap-1 ml-auto" data-id="element-1362">
              <button onClick={() => setAssistantOpen(!assistantOpen)} className="p-2 rounded-md hover:bg-gray-50 transition-colors relative group" data-id="element-1363">
                <HelpCircleIcon size={18} className="text-gray-500" data-id="element-1364" />
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1365">
                  Help
                </span>
              </button>

              <div className="relative" ref={notificationsRef} data-id="element-1366">
                <button onClick={() => setNotificationsOpen(!notificationsOpen)} className="p-2 rounded-md hover:bg-gray-50 transition-colors relative group" data-id="element-1367">
                  <BellIcon size={18} className="text-gray-500" data-id="element-1368" />
                  {unreadNotifications.length > 0 && <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-blue-600 rounded-full" data-id="element-1369"></span>}
                  {!notificationsOpen && <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1370">
                      Notifications
                    </span>}
                </button>

                {notificationsOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setNotificationsOpen(false)} data-id="element-1371" />
                    <div className="absolute right-0 mt-2 w-[420px] bg-white rounded-xl shadow-xl border border-gray-200 z-20 overflow-hidden" data-id="element-1372">
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 pt-4 pb-3" data-id="element-1373">
                        <h3 className="text-base font-semibold text-gray-900" data-id="element-1374">
                          Notifications
                        </h3>
                        <div className="flex items-center gap-2" data-id="element-1375">
                          {unreadNotifications.length > 0 && <button className="text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md transition-colors" data-id="element-1376">
                              Mark {unreadNotifications.length} as read
                            </button>}
                          <button onClick={() => {
                        setNotificationsOpen(false);
                        navigate('/settings');
                      }} className="text-xs font-medium text-gray-600 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors" data-id="element-1377">
                            View preferences
                          </button>
                        </div>
                      </div>

                      {/* Tabs */}
                      <div className="flex px-5 gap-2 mb-1" data-id="element-1378">
                        <button onClick={() => setNotificationTab('unread')} className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${notificationTab === 'unread' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1379">
                          Unread
                        </button>
                        <button onClick={() => setNotificationTab('all')} className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${notificationTab === 'all' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1380">
                          All
                        </button>
                      </div>

                      {/* Notification List */}
                      <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-100" data-id="element-1381">
                        {displayNotifications.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-1382">
                            <p className="text-sm text-gray-500" data-id="element-1383">
                              No notifications
                            </p>
                          </div> : displayNotifications.slice(0, 5).map(notification => <div key={notification.id} className="px-5 py-3.5 hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-1384">
                                <div className="flex items-start gap-2" data-id="element-1385">
                                  {notification.unread && <span className="w-2 h-2 rounded-full bg-blue-600 mt-1.5 flex-shrink-0" data-id="element-1386"></span>}
                                  <div className={`flex-1 min-w-0 ${!notification.unread ? 'ml-4' : ''}`} data-id="element-1387">
                                    <p className="text-sm text-gray-800 leading-relaxed" data-id="element-1388">
                                      {notification.message}
                                    </p>
                                    <div className="flex items-center justify-between mt-1.5" data-id="element-1389">
                                      <span className="text-xs text-gray-400" data-id="element-1390">
                                        {notification.date}
                                      </span>
                                      <button className="p-1 rounded hover:bg-gray-200 transition-colors" onClick={e => e.stopPropagation()} data-id="element-1391">
                                        <MoreHorizontalIcon size={14} className="text-gray-400" data-id="element-1392" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>)}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50" data-id="element-1393">
                        <span className="text-xs text-gray-500" data-id="element-1394">
                          {unreadNotifications.length} of {notifications.length}{' '}
                          unread notifications
                        </span>
                        <button onClick={() => {
                      setNotificationsOpen(false);
                      navigate('/notifications');
                    }} className="text-xs font-medium text-blue-600 hover:text-blue-700" data-id="element-1395">
                          View all
                        </button>
                      </div>
                    </div>
                  </>}
              </div>

              {/* Create Menu */}
              <div className="relative group" data-id="element-1396">
                <button onClick={() => setCreateMenuOpen(!createMenuOpen)} className="p-1.5 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-1397">
                  <PlusIcon size={16} className="text-white" data-id="element-1398" />
                </button>
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1399">
                  Create
                </span>
                {createMenuOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setCreateMenuOpen(false)} data-id="element-1400" />
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20" data-id="element-1401">
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/invoice-form');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1402">
                        <div className="flex items-center" data-id="element-1403">
                          <FileTextIcon size={18} className="mr-3 text-gray-500" data-id="element-1404" />
                          <span className="font-medium" data-id="element-1405">Invoice</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1406">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1407">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1408">
                            I
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=recurring');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1409">
                        <div className="flex items-center" data-id="element-1410">
                          <RepeatIcon size={18} className="mr-3 text-gray-500" data-id="element-1411" />
                          <span className="font-medium" data-id="element-1412">Subscription</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1413">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1414">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1415">
                            S
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=single');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1416">
                        <div className="flex items-center" data-id="element-1417">
                          <LinkIcon size={18} className="mr-3 text-gray-500" data-id="element-1418" />
                          <span className="font-medium" data-id="element-1419">Payment link</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1420">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1421">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1422">
                            L
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=charge');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1423">
                        <div className="flex items-center" data-id="element-1424">
                          <CreditCardIcon size={18} className="mr-3 text-gray-500" data-id="element-1425" />
                          <span className="font-medium" data-id="element-1426">Charge</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1427">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1428">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1429">
                            P
                          </kbd>
                        </div>
                      </button>
                      <button onClick={() => {
                    setCreateMenuOpen(false);
                    navigate('/payment-requests?tab=security-hold');
                  }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1430">
                        <div className="flex items-center" data-id="element-1431">
                          <ShieldIcon size={18} className="mr-3 text-gray-500" data-id="element-1432" />
                          <span className="font-medium" data-id="element-1433">Security hold</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1434">
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1435">
                            ⌘
                          </kbd>
                          <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1436">
                            O
                          </kbd>
                        </div>
                      </button>
                    </div>
                  </>}
              </div>

              {/* User Menu */}
              <div className="relative ml-2" data-id="element-1437">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1438">
                  <img src="https://cdn.magicpatterns.com/uploads/dBrfzXiyML7jgesvo4qJJW/Screenshot_2026-03-17_at_06.40.16.png" alt="User profile" className="w-8 h-8 rounded-full object-cover" data-id="element-1439" />
                  <span className="ml-2 hidden sm:block text-sm font-medium text-gray-700" data-id="element-1440">
                    Simon
                  </span>
                  <ChevronDownIcon size={14} className={`ml-1 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} data-id="element-1441" />
                </button>
                {userMenuOpen && <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} data-id="element-1442" />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-1443">
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=company');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1444">
                        <UsersIcon size={16} className="mr-3 text-gray-500" data-id="element-1445" />
                        Account
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=company');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1446">
                        <BarChartIcon size={16} className="mr-3 text-gray-500" data-id="element-1447" />
                        Report
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1448">
                        <SettingsIcon size={16} className="mr-3 text-gray-500" data-id="element-1449" />
                        Settings
                      </button>
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings?tab=security');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1450">
                        <ShieldIcon size={16} className="mr-3 text-gray-500" data-id="element-1451" />
                        Security
                      </button>
                      <div className="border-t border-gray-100 my-1" data-id="element-1452" />
                      <button onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                    navigate('/');
                  }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-1453">
                        <LogOutIcon size={16} className="mr-3" data-id="element-1454" />
                        Logout
                      </button>
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar - sits below header, aligned with logo column */}
      <div className={`
          fixed top-14 bottom-0 left-0 w-60 bg-[#0F1B2D] border-r border-[#1A2A40] z-20 transform transition-transform duration-200 ease-in-out overflow-y-auto
          ${menuOpen ? 'translate-x-0 shadow-lg lg:shadow-none' : '-translate-x-full lg:translate-x-0'}
        `} data-id="element-1455">
        <nav className="py-4 px-2" data-id="element-1456">
          {/* Dashboard */}
          <button onClick={() => navigate('/overview')} className={navItemClass(isActive('/overview'))} data-id="element-1457">
            <LayoutGridIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1458" />
            Dashboard
          </button>

          {/* Section: Merchants & Partners */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1459">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1460">
              Merchants & Partners
            </span>
          </div>
          <button onClick={() => navigate('/payment-requests')} className={navItemClass(isActive('/payment-requests'))} data-id="element-1461">
            <CreditCardIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1462" />
            Payment requests
          </button>

          {/* Terminals */}
          <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className={navParentClass(isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect'))} data-id="element-1463">
            <div className="flex items-center" data-id="element-1464">
              <TerminalIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1465" />
              Terminals
            </div>
            <ChevronDownIcon size={14} className={`text-gray-500 transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} data-id="element-1466" />
          </button>
          {terminalsExpanded && <div className="pl-7" data-id="element-1467">
              <button onClick={() => navigate('/virtual-terminal')} className={subNavItemClass(isActive('/virtual-terminal'))} data-id="element-1468">
                Virtual
              </button>
              <button onClick={() => navigate('/physical-terminals')} className={subNavItemClass(isActive('/physical-terminals'))} data-id="element-1469">
                Physical
              </button>
              <button onClick={() => navigate('/softpos-terminals')} className={subNavItemClass(isActive('/softpos-terminals'))} data-id="element-1470">
                SoftPos
              </button>
              <button onClick={() => navigate('/connect')} className={subNavItemClass(isActive('/connect'))} data-id="element-1471">
                Connect
              </button>
            </div>}

          <button onClick={() => navigate('/invoices')} className={navItemClass(isActive('/invoices'))} data-id="element-1472">
            <FileTextIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1473" />
            Invoices
          </button>
          <button onClick={() => navigate('/bills')} className={navItemClass(isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/'))} data-id="element-1474">
            <FileIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1475" />
            Bills
          </button>

          {/* Relationships */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1476">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1477">
              Relationships
            </span>
          </div>
          <button onClick={() => navigate('/customers')} className={navItemClass(isActive('/customers'))} data-id="element-1478">
            <UsersIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1479" />
            Customers
          </button>
          <button onClick={() => navigate('/suppliers')} className={navItemClass(isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form'))} data-id="element-1480">
            <TruckIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1481" />
            Suppliers
          </button>

          {/* Payments */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1482">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1483">
              Payments
            </span>
          </div>
          <button onClick={() => setBalanceExpanded(!balanceExpanded)} className={navParentClass(isBalanceActive())} data-id="element-1484">
            <div className="flex items-center" data-id="element-1485">
              <BanknoteIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1486" />
              Balance
            </div>
            <ChevronDownIcon size={14} className={`text-gray-500 transition-transform ${balanceExpanded ? 'rotate-180' : ''}`} data-id="element-1487" />
          </button>
          {balanceExpanded && <div className="pl-7" data-id="element-1488">
              <button onClick={() => navigate('/balance')} className={subNavItemClass(isActive('/balance'))} data-id="element-1489">
                Summary
              </button>
              <button onClick={() => navigate('/balance-activity')} className={subNavItemClass(isActive('/balance-activity'))} data-id="element-1490">
                Activity
              </button>
              <button onClick={() => navigate('/payouts')} className={subNavItemClass(isActive('/payouts'))} data-id="element-1491">
                Payouts
              </button>
            </div>}
          <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className={navParentClass(isTransactionsActive())} data-id="element-1492">
            <div className="flex items-center" data-id="element-1493">
              <ArrowLeftRightIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1494" />
              Transactions
            </div>
            <ChevronDownIcon size={14} className={`text-gray-500 transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} data-id="element-1495" />
          </button>
          {transactionsExpanded && <div className="pl-7" data-id="element-1496">
              <button onClick={() => navigate('/payment-analytics')} className={subNavItemClass(isActive('/payment-analytics'))} data-id="element-1497">
                Payment analytics
              </button>
              <button onClick={() => navigate('/sales')} className={subNavItemClass(isActive('/sales') || location.pathname.startsWith('/sale-detail/'))} data-id="element-1498">
                Sales
              </button>

              <button onClick={() => navigate('/disputes')} className={subNavItemClass(isActive('/disputes') || location.pathname.startsWith('/dispute-detail/'))} data-id="element-1499">
                Disputes
              </button>
              <button onClick={() => navigate('/refunds')} className={subNavItemClass(isActive('/refunds') || location.pathname.startsWith('/refund-detail/'))} data-id="element-1500">
                Refunds
              </button>
            </div>}

          {/* E-Commerce */}
          <div className="mt-5 mb-1.5 px-3" data-id="element-1501">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1502">
              E-Commerce
            </span>
          </div>
          <button onClick={() => navigate('/shops')} className={navItemClass(isActive('/shops'))} data-id="element-1503">
            <ShoppingBagIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1504" />
            Shops
          </button>
          <button onClick={() => navigate('/orders')} className={navItemClass(isActive('/orders'))} data-id="element-1505">
            <ShoppingCartIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1506" />
            Orders
          </button>
          <button onClick={() => navigate('/campaigns')} className={navItemClass(isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form'))} data-id="element-1507">
            <MegaphoneIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1508" />
            Campaigns
          </button>

          {/* Reports & Analytics */}
          <div className="mt-5 mb-1.4 px-3" data-id="element-1509">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1510">
              Reports & Analytics
            </span>
          </div>
          <button onClick={() => setReportingExpanded(!reportingExpanded)} className={navParentClass(isReportingActive())} data-id="element-1511">
            <div className="flex items-center" data-id="element-1512">
              <BarChartIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1513" />
              Reporting
            </div>
            <ChevronDownIcon size={14} className={`text-gray-500 transition-transform ${reportingExpanded ? 'rotate-180' : ''}`} data-id="element-1514" />
          </button>
          {reportingExpanded && <div className="pl-7" data-id="element-1515">
              <button onClick={() => navigate('/reports')} className={subNavItemClass(isActive('/reports'))} data-id="element-1516">
                Reports
              </button>
              <button onClick={() => navigate('/custom-metrics')} className={subNavItemClass(isActive('/custom-metrics'))} data-id="element-1517">
                Custom metrics
              </button>
              <button onClick={() => navigate('/nadi')} className={subNavItemClass(isActive('/nadi'))} data-id="element-1518">
                Nadi
              </button>
            </div>}

          {/* System */}
          <div className="mt-4 mb-1 px-4" data-id="element-1519">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1520">
              System
            </span>
          </div>
          <button onClick={() => navigate('/settings')} className={navItemClass(isActive('/settings'))} data-id="element-1521">
            <SettingsIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1522" />
            Settings
          </button>
          <button onClick={() => navigate('/tutorials')} className={navItemClass(isActive('/tutorials'))} data-id="element-1523">
            <GraduationCapIcon size={16} className="mr-3 flex-shrink-0" data-id="element-1524" />
            Tutorials
          </button>
          <div className="h-6" data-id="element-1525" />
        </nav>
      </div>

      {/* Main content - no box, just flows */}
      <div className="lg:pl-60 pt-14 min-h-screen" data-id="element-1526">
        <div className="px-6 lg:px-8 py-6" data-id="element-1527">{children}</div>
      </div>

      {/* AI Assistant Panel */}
      {assistantOpen && <>
          <div className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity" onClick={() => setAssistantOpen(false)} data-id="element-1528" />
          <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col" data-id="element-1529">
            <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-1530">
              <div className="flex items-center gap-2" data-id="element-1531">
                <div className="w-8 h-8 bg-[#F7941D] rounded-full flex items-center justify-center" data-id="element-1532">
                  <span className="text-white text-sm font-semibold" data-id="element-1533">✨</span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-1534">
                  Assistant
                </h2>
              </div>
              <button onClick={() => setAssistantOpen(false)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1535">
                <XIcon size={20} className="text-gray-500" data-id="element-1536" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1537">
              <div className="space-y-4" data-id="element-1538">
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-1539">
                  <p className="text-sm text-gray-700 mb-2" data-id="element-1540">
                    Hi, I'm Rabbit Gateway Assistant. 👋
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1541">
                    What do you need help with? Select a topic or type your
                    question below.
                  </p>
                </div>
                <div className="space-y-2" data-id="element-1542">
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1543">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1544">
                      Contact merchant support
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1545">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1546">
                      My verification status
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1547">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1548">
                      Change business type
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1549">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1550">
                      My settlement is missing
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1551">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1552">
                      Update bank account for settlement
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1553">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1554">
                      Malaysia payment regulations
                    </p>
                  </button>
                </div>
                <div className="pt-4 border-t border-gray-200" data-id="element-1555">
                  <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-1556">
                    RECENT
                  </p>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#F7941D] hover:bg-orange-50 transition-colors" data-id="element-1557">
                    <p className="text-sm text-gray-700" data-id="element-1558">
                      How do I enable FPX for my customers?
                    </p>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200" data-id="element-1559">
              <div className="relative" data-id="element-1560">
                <input type="text" placeholder={placeholderTexts[placeholderIndex]} className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D] focus:border-transparent transition-all" key={placeholderIndex} data-id="element-1561" />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600" data-id="element-1562">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" data-id="element-1563">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" data-id="element-1564" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-10 lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-1565"></div>}
    </div>;
}